import React, {useState} from 'react'
import {Button} from "primereact/button";
import LiveView from "../liveview/LiveView";
import {getFromLocalStorageWithDefault, saveToLocalStorage} from "../../../../util/localStorageZugriff";
import ZoomSteuerung, {useLiveZoom, ZOOM_STUFEN} from "./ZoomSteuerung";
import {useVollbild} from "./browserVollbild";
import {LeisteAusblendenKnopf, LeisteEinblendenKnopf, useLeisteAusblendbar} from "./VollbildLeisteKnoepfe";

const KEY_DARSTELLUNG = "dh_public_live_darstellung";

const DARSTELLUNGEN = [
    // die neue TV-Ansicht steht ganz links und ist der Standard
    {id: "tvmodern", label: "TV", icon: "pi pi-video"},
    {id: "kachel", label: "Kacheln", icon: "pi pi-th-large"},
    {id: "liste", label: "Liste", icon: "pi pi-list"},
    {id: "tv", label: "TV", icon: "pi pi-desktop"},
];

// in der App gibt es die Listenansicht nicht
const getDarstellungen = (appCall) => appCall ? DARSTELLUNGEN.filter(d => d.id !== "liste") : DARSTELLUNGEN;

const ladeDarstellung = (appCall) => {
    try {
        const gespeichert = getFromLocalStorageWithDefault(KEY_DARSTELLUNG, undefined);
        if (getDarstellungen(appCall).some(d => d.id === gespeichert)) {
            return gespeichert;
        }
    } catch (e) {
        // ohne Speicher (z.B. privater Modus) gilt der Standard
    }
    return "tvmodern";
}

/**
 * Darstellung der Live-Spiele (Kacheln, Liste, ...), wird im Browser gemerkt.
 * @returns [darstellung, waehleDarstellung]
 */
export function useLiveDarstellung(appCall) {
    const [darstellung, setDarstellung] = useState(() => ladeDarstellung(appCall));
    const waehleDarstellung = (id) => {
        setDarstellung(id);
        try {
            saveToLocalStorage(KEY_DARSTELLUNG, id);
        } catch (e) {
            // ohne Speicher (z.B. privater Modus) gilt die Auswahl nur bis zum Neuladen
        }
    }
    return [darstellung, waehleDarstellung];
}

/**
 * Segment-Schalter für die Darstellung der Live-Spiele.
 */
export function LiveDarstellungAuswahl({appCall, darstellung, waehleDarstellung}) {
    return <div className="dhpt-segment" role="radiogroup" aria-label="Darstellung der Live-Spiele">
        {getDarstellungen(appCall).map(d =><button key={d.id} type="button" role="radio" aria-checked={darstellung === d.id}
                                        className="dhpt-segment-knopf" title={d.label} onClick={() => waehleDarstellung(d.id)}>
            <i className={d.icon} aria-hidden="true"/>
            <span className="dhpt-segment-label">{d.label}</span>
        </button>)}
    </div>
}

/**
 * Live-Ansicht im neuen Design (Turnier und Veranstalter). Statt eine eigene Seite zu öffnen, wird die Live-Ansicht
 * innerhalb der Seite über den ganzen Bildschirm gelegt und, sofern der Browser es erlaubt, echtes Vollbild eingeschaltet.
 * LiveView bleibt dabei eingehängt, damit beim Umschalten keine neue Verbindung aufgebaut wird.
 * Die Auswahl der Darstellung (Kacheln, Liste, ...) kommt von hier, nicht aus LiveView, und wird im Browser gemerkt,
 * ebenso der Zoom (gilt für die normale Ansicht und das Vollbild, siehe useLiveZoom).
 * In der App gibt es keine Liste und kein Vollbild.
 */
export default function LiveBereich({tournamentId, eventOperatorId, boardId, titel, appCall}) {

    // Vollbild über die ganze Seite, außerhalb der App auch das echte Vollbild des Browsers
    const [vollbild, starteVollbild, schliesseVollbild] = useVollbild(appCall);
    // Leiste im Vollbild ausblendbar, damit mehr Platz für die Spiele bleibt
    const [leisteAus, setLeisteAus] = useLeisteAusblendbar(vollbild);
    const [darstellung, waehleDarstellung] = useLiveDarstellung(appCall);
    // Zoom in der normalen Ansicht und im Vollbild, z.B. für einen großen Bildschirm, wird im Browser gemerkt
    const [zoomIndex, setzeZoom] = useLiveZoom(appCall);
    // Platz in der Leiste, in den LiveView das Zahnrad der TV-Ansichten zeichnet (neben dem Zoom)
    const [zahnradZiel, setZahnradZiel] = useState(null);

    const getDarstellungAuswahl = () => {
        return <LiveDarstellungAuswahl appCall={appCall} darstellung={darstellung} waehleDarstellung={waehleDarstellung}/>
    }

    return <div className={vollbild ? "dhpt-vollbild" + (leisteAus ? " dhpt-vollbild-ohne-leiste" : "") : "dhpt-live"}>
        {/* eigener Platz vor der Leiste, damit LiveView beim Ein-/Ausblenden eingehängt bleibt */}
        {vollbild && leisteAus && <LeisteEinblendenKnopf oben={true} onClick={() => setLeisteAus(false)}/>}
        {vollbild
            // nur per CSS ausgeblendet, damit der Platz für das Zahnrad der TV-Ansichten erhalten bleibt
            ? <div className={"dhpt-vollbild-leiste" + (leisteAus ? " dhpt-ausgeblendet" : "")}>
                <span className="dhpt-vollbild-titel"><span className="dhpt-chip dhpt-chip-live">Live</span> {titel}</span>
                {getDarstellungAuswahl()}
                <span ref={setZahnradZiel} className="dhpt-zahnrad-ziel"/>
                <ZoomSteuerung zoomIndex={zoomIndex} setZoomIndex={setzeZoom}/>
                <LeisteAusblendenKnopf onClick={() => setLeisteAus(true)}/>
                <Button icon="pi pi-times" label="Schließen" className="p-button-secondary" onClick={schliesseVollbild}/>
            </div>
            : <div className="dhpt-live-leiste">
                {getDarstellungAuswahl()}
                <span className="dhpt-live-zoom">
                    <span ref={setZahnradZiel} className="dhpt-zahnrad-ziel"/>
                    <ZoomSteuerung zoomIndex={zoomIndex} setZoomIndex={setzeZoom} klein={true}/>
                    {/* in der App kein Vollbild */}
                    {!appCall && <Button icon="pi pi-window-maximize" label="Vollbild" className="p-button-sm p-button-outlined dhpt-button-hell" onClick={starteVollbild}/>}
                </span>
            </div>
        }
        <div className={vollbild ? "dhpt-vollbild-inhalt" : undefined}>
            {/* gezoomt wird in der normalen Ansicht und im Vollbild, LiveView bleibt dabei eingehängt */}
            <div style={{zoom: ZOOM_STUFEN[zoomIndex]}}>
                <LiveView appCall={appCall === true} tournamentid={tournamentId} eventOperatorId={eventOperatorId} boardId={boardId} showEmbedded={true} zahnradZiel={zahnradZiel}
                          viewModusVorgabe={darstellung} ohneAnsichtAuswahl={true}/>
            </div>
        </div>
    </div>
}
